import styled from 'styled-components';

import { ThemeProp } from '~/theme/Themes';

// Wraps a whole A2UI surface inline in the chat. Vertical margin separates it from the prose
// above/below (the drawer renders message parts with no gap between them). `$inert` dims +
// blocks interaction while the agent is streaming or after submit.
//
// `isolation: isolate` pins this to its own stacking context. Without it, a flex + overflow:hidden
// descendant (e.g. a ChoicePicker's NextUI dropdown button) can get promoted to an independent
// compositor layer and paint on top of the sidebar's docked footer despite the footer's higher
// z-index, since the browser's layer paint order doesn't always follow DOM stacking order here.
export const SurfaceContainer = styled.div<{ $inert?: boolean }>`
  margin: 1rem 0;
  isolation: isolate;
  ${({ $inert }) => ($inert ? 'opacity: 0.6; pointer-events: none;' : '')}
`;

// Inline error line for input renderers without a native `error` prop slot.
export const ErrorLine = styled.div`
  color: ${(props: ThemeProp) => props.theme.colors.system.error.c500};
  font-size: ${(props: ThemeProp) => props.theme.fontSize.xs};
  margin-top: 0.25rem;
`;

// Label for renderers without a native `label` prop slot (e.g. VIQDateRangePicker).
export const FieldLabel = styled.div`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.semibold};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  margin-bottom: 0.25rem;
`;

// Layout primitives overriding the basic catalog's stock Text/Row/Column/Card.
// As standalone prose it inherits the drawer's MessageBubble typography (neutrals.black); as a
// Button label (see VisitiqButton) it inherits the button's own color/font so it doesn't fight
// the button's styling with a hardcoded color.
export const CatalogText = styled.span`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  color: inherit;
  font-family: inherit;
  line-height: normal;
`;

interface CatalogRowProps {
  $justify?: string;
}

export const CatalogRow = styled.div<CatalogRowProps>`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
  ${({ $justify }: CatalogRowProps) => ($justify ? `justify-content: ${$justify};` : '')}
`;

export const CatalogColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const CatalogCard = styled.div`
  background: ${(props: ThemeProp) => props.theme.colors.neutrals.white};
  border: 1px solid ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
`;

// Muted card shown when a surface can't be rendered (unknown component, or a renderer threw).
export const FallbackCard = styled(CatalogCard)`
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
`;
